import React, { useEffect, useState } from "react";
import { Phone, TrendingUp, Clock } from "lucide-react";
import api from "../lib/api";

/**
 * CallPerformanceCard — per-agent call metrics for the Reports page.
 *
 * Shows total / inbound / outbound / answered / avg-duration / answer-rate
 * per agent for the last `days` days. Admins + leaders see it; salespeople
 * don't (their view is scoped to their own Dashboard widget).
 *
 * Data source: GET /api/telecmi/agent-stats?days=30
 */

const fmtDuration = (seconds) => {
  if (!seconds || seconds < 1) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const CallPerformanceCard = () => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/telecmi/agent-stats?days=${days}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [days]);

  const rows = (data?.agents || []).filter((r) => r.total_calls > 0);
  // Aggregate totals across all rows for the header summary.
  const totals = rows.reduce((acc, r) => {
    acc.total += r.total_calls;
    acc.answered += r.answered_calls;
    acc.missed += r.missed_calls;
    acc.totalDuration += (r.avg_duration_s || 0) * (r.answered_calls || 0);
    return acc;
  }, { total: 0, answered: 0, missed: 0, totalDuration: 0 });
  const avgAnswerRate = totals.total
    ? Math.round((totals.answered / totals.total) * 1000) / 10
    : 0;
  const avgDur = totals.answered ? Math.round(totals.totalDuration / totals.answered) : 0;

  return (
    <div className="card p-5 mt-6" data-testid="call-performance-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-[--text-primary] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Phone size={16} className="text-indigo-600" /> Call Performance
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-2">
            last {days} days
          </span>
        </h3>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2 py-1 text-[10px] font-bold rounded-md ${
                days === d
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`call-perf-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary strip */}
      {!loading && !error && rows.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          <SummaryTile label="Total calls" value={totals.total} />
          <SummaryTile label="Answered" value={totals.answered} accent="text-emerald-600" />
          <SummaryTile label="Avg duration" value={fmtDuration(avgDur)} />
          <SummaryTile label="Answer rate" value={`${avgAnswerRate}%`} accent={avgAnswerRate >= 80 ? "text-emerald-600" : avgAnswerRate >= 50 ? "text-amber-600" : "text-red-600"} />
        </div>
      )}

      {loading && (
        <div className="py-8 text-center text-[12px] text-slate-500 italic">Loading call metrics…</div>
      )}
      {error && (
        <div className="py-4 text-center text-[12px] text-red-600 italic">{error}</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="py-8 text-center text-[12px] text-slate-500 italic" data-testid="call-perf-empty">
          No calls in the last {days} days.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="call-perf-table">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 uppercase font-bold tracking-wider">
                <th className="py-2 pr-3 text-left">Agent</th>
                <th className="py-2 px-2 text-right">Total</th>
                <th className="py-2 px-2 text-right">In</th>
                <th className="py-2 px-2 text-right">Out</th>
                <th className="py-2 px-2 text-right">Answered</th>
                <th className="py-2 px-2 text-right">Missed</th>
                <th className="py-2 px-2 text-right">Avg dur.</th>
                <th className="py-2 pl-2 text-right">Answer%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.agent_id || r.agent_name}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`call-perf-row-${r.agent_id || r.agent_name}`}
                >
                  <td className="py-2 pr-3">
                    <div className="font-bold text-slate-800">{r.agent_name}</div>
                    {r.agent_email && <div className="text-[10px] text-slate-500">{r.agent_email}</div>}
                  </td>
                  <td className="py-2 px-2 text-right font-mono font-bold">{r.total_calls}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-600">{r.inbound}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-600">{r.outbound}</td>
                  <td className="py-2 px-2 text-right font-mono text-emerald-700">{r.answered_calls}</td>
                  <td className="py-2 px-2 text-right font-mono text-red-600">{r.missed_calls || 0}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-600">{fmtDuration(r.avg_duration_s)}</td>
                  <td className="py-2 pl-2 text-right">
                    <span className={`font-bold ${r.answer_rate_pct >= 80 ? "text-emerald-600" : r.answer_rate_pct >= 50 ? "text-amber-600" : "text-red-600"}`}>
                      {r.answer_rate_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, accent }) => (
  <div className="bg-slate-50 border border-slate-100 rounded-lg p-2 text-center">
    <div className={`text-lg font-extrabold ${accent || "text-slate-800"}`} style={{ fontFamily: "Outfit" }}>
      {typeof value === "number" ? value.toLocaleString() : value}
    </div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">{label}</div>
  </div>
);

export default CallPerformanceCard;
